import type { HierarchyPathItem, Product } from '@shared/gql/document-nodes'
import type { FC, ReactNode } from 'react'

import { useReactiveVar } from '@apollo/client'
import { getConfig } from '@shared/config'
import { I18nLang as Lang } from '@shared/domain/i18n'
import { Alert, AlertType, minWidthFromTheme } from '@sok/design-system'
import { LanguageSelector } from 'components/common/LanguageSelector/LanguageSelector'
import { CartSidebarLayout } from 'components/layout/CartSidebarLayout'
import { localizationVar } from 'lib/apollo/reactive-vars'
import { NextSeo } from 'next-seo'
import { useEffect, useRef } from 'react'
import { useTranslation } from 'react-i18next'
import styled, { useTheme } from 'styled-components'
import { useCanonicalUrl } from 'utils/hooks/use-canonical-url'
import { sCloudProductImageLoader } from 'utils/next-image-loaders'
import { useGetOrderEditState } from 'utils/order/use-get-order-edit-state'

import { ProductPageBreadcrumb } from './ProductPageBreadcrumb'

interface Props {
  children?: ReactNode
  error?: boolean
  hierarchyPath?: Pick<HierarchyPathItem, 'id' | 'name' | 'slug'>[] | null
  product?: Pick<Product, 'ean' | 'name'> | null
}

const { domain } = getConfig()

export const ProductPageTemplate: FC<Props> = ({ children, error, hierarchyPath, product }) => {
  const theme = useTheme()

  const seoCanonical = useCanonicalUrl()
  const { t } = useTranslation()
  const orderEditState = useGetOrderEditState()

  const localization = useReactiveVar(localizationVar)

  const { isSIdEnabled } = domain

  const productName = product?.name || ''
  const productDescription = t('Product info SEO', { productName })

  const openGraph = {
    type: 'website',
    ...(product?.ean
      ? {
          images: [
            {
              alt: productName,
              url: sCloudProductImageLoader({
                height: 630,
                padded: true,
                quality: 75,
                src: product.ean || '',
                width: 1200,
              }),
            },
          ],
        }
      : {}),
  }

  const containerRef = useRef<HTMLDivElement>(null)
  useEffect(() => {
    /** Reset scroll when product changes */
    containerRef.current?.scrollTo({ top: 0 })
  }, [product?.ean])

  return (
    <>
      <NextSeo
        title={
          (error && `${t('pageTitle_page-error')} | ${theme.seoTags.title}`) ||
          (productName
            ? `${productName} | ${theme.seoTags.title}`
            : `${t('pageTitle_product-not-found')} | ${theme.seoTags.title}`)
        }
        description={productDescription}
        canonical={seoCanonical}
        openGraph={openGraph}
      />
      <CartSidebarLayout>
        <StyledWrapper data-test-id="product-page-container" ref={containerRef}>
          <StyledBreadcrumbWrapper>
            <ProductPageBreadcrumb hierarchyPath={hierarchyPath} />
            {isSIdEnabled && <LanguageSelector />}
          </StyledBreadcrumbWrapper>

          {localization === Lang.SV && (
            <StyledAlert
              message={t('Product data quality could differ according to language change')}
              type={AlertType.INFO}
              isMobile
              closable
            />
          )}

          {children}

          <StyledFooter hasPadding={orderEditState.isOrderEditActive} />
        </StyledWrapper>
      </CartSidebarLayout>
    </>
  )
}

const StyledBreadcrumbWrapper = styled.div(({ theme }) => ({
  backgroundColor: theme.color.lightestGrey100,
  display: 'flex',
  gap: theme.spacings.small,
  padding: `0 ${theme.spacings.small}`,

  [LanguageSelector]: {
    marginLeft: 'auto',
  },

  [minWidthFromTheme(theme).desktop]: {
    padding: 0,
  },
}))

const StyledWrapper = styled.div(({ theme }) => ({
  /**
   * Negate the top margin created by `BaseLayout`, because product page
   * breadcrumbs are on the outside the main container
   */
  [minWidthFromTheme(theme).desktop]: {
    marginTop: `-${theme.spacings.medium}`,
  },
}))

const StyledAlert = styled(Alert)({
  lineHeight: '2',
})

interface StyledFooterPaddingProps {
  hasPadding: boolean
}

const StyledFooter = styled.div<StyledFooterPaddingProps>`
  padding-bottom: ${({ hasPadding }) => (hasPadding ? '30px' : '0px')};
  ${({ theme }) => minWidthFromTheme(theme).tablet} {
    padding-bottom: ${({ hasPadding }) => (hasPadding ? '80px' : '0px')};
  }
`
